import {
    ISignalsChartDefinition,
    ISignalsMontageCard,
    ISignalsSmallCard,
    ISignalsTextCard,
    ISignalsDriversOnTheRiseResponse,
    ISignalsTopicsOnTheRiseResponse,
    SignalsEndpoints,
    SignalsObjectType,
    SignalsRequest,
    SignalStatType,
} from "../SignalsModels";
import { BaseObjectStoreParams } from "./BaseObjectStore";
import TextCardStore from "./TextCard/TextCardStore";
import MontageCardStore from "./MontageCard/MontageCardStore";
import ChartCardStore from "./ChartCard/ChartCardStore";
import TableCardStore from "./TableCard/TableCardStore";
import SmallCardStore, {
    SmallCardFormattedData,
    SmallCardRequestData,
} from "./SmallCard/SmallCardStore";
import { toIsoByDateReference } from "utils/DateTimeUtils";
import { DatePickerComponentStore } from "stores/ComponentStores/DatePickerComponentStore";
import moment from "moment";
import UniqueCardStore from "./UniqueCard/UniqueCardStore";
import TopicsOnTheRise from "./UniqueCard/TopicsOnTheRise";
import DriversOnTheRise from "./UniqueCard/DriversOnTheRise";
import { ApplicationFiltersStore } from "stores/ApplicationFilters/ApplicationFiltersStore";

export const createComponentStoreFromBaseObject = ({
    baseObject,
    rootStore,
    options,
    initialData,
    refreshOnCreate,
}: BaseObjectStoreParams<any>) => {
    switch (baseObject.objectType) {
        case SignalsObjectType.TextCard:
            return new TextCardStore({
                baseObject: baseObject as ISignalsTextCard,
                rootStore,
                options,
                initialData,
                refreshOnCreate,
            });
        case SignalsObjectType.MontageCard:
            return new MontageCardStore({
                baseObject: baseObject as ISignalsMontageCard,
                rootStore,
                options,
                initialData,
                refreshOnCreate,
            });
        case SignalsObjectType.SmallCard:
            return new SmallCardStore({
                baseObject: baseObject as ISignalsSmallCard,
                rootStore,
                options,
                initialData,
                refreshOnCreate,
            });
        case SignalsObjectType.Chart:
            return new ChartCardStore({
                baseObject: baseObject as ISignalsChartDefinition,
                rootStore,
                options,
                initialData,
                refreshOnCreate,
            });
        case SignalsObjectType.Table:
            return new TableCardStore({
                baseObject: baseObject as ISignalsChartDefinition,
                rootStore,
                options,
                initialData,
                refreshOnCreate,
            });
        case SignalsObjectType.Unique:
            if (
                baseObject.dataEndpoint ===
                SignalsEndpoints.legacyTopicReports.TopicsOnTheRiseID
            ) {
                const buildTopicsOnTheRiseState = (
                    applicationFiltersStore: ApplicationFiltersStore,
                ): SignalsRequest => {
                    const [startDate, endDate] = getFormattedDates(
                        applicationFiltersStore.quickFiltersStore
                            .datePickerStore!,
                    );

                    const filters = applicationFiltersStore.toRequestObject();

                    return {
                        ...filters,
                        startDate: startDate ?? "",
                        endDate: endDate ?? "",
                    };
                };
                const getTopicsOnTheRiseCacheKeyPart:
                    | ((
                          applicationFiltersStore: ApplicationFiltersStore,
                          state?: SignalsRequest,
                      ) => unknown)
                    | undefined = undefined;

                return new UniqueCardStore({
                    baseObject: baseObject,
                    rootStore,
                    options,
                    initialData,
                    refreshOnCreate,
                    getStateFunction: (applicationFiltersStore) =>
                        buildTopicsOnTheRiseState(applicationFiltersStore),
                    getDataFunction: async (
                        signalsService,
                        applicationFiltersStore,
                        state,
                    ): Promise<ISignalsTopicsOnTheRiseResponse> => {
                        const requestState =
                            state ??
                            buildTopicsOnTheRiseState(applicationFiltersStore);
                        const res = await signalsService.getTopicsOnTheRise(
                            requestState,
                        );

                        return res;
                    },
                    getCacheKeyPartFunction: getTopicsOnTheRiseCacheKeyPart,
                    component: TopicsOnTheRise,
                });
            }
            if (
                baseObject.dataEndpoint ===
                SignalsEndpoints.unique.DriversOnTheRiseID
            ) {
                const buildDriversOnTheRiseState = (
                    applicationFiltersStore: ApplicationFiltersStore,
                ): SignalsRequest => {
                    const [startDate, endDate] = getFormattedDates(
                        applicationFiltersStore.quickFiltersStore
                            .datePickerStore!,
                    );

                    const filters = applicationFiltersStore.toRequestObject();

                    return {
                        ...filters,
                        startDate: startDate ?? "",
                        endDate: endDate ?? "",
                    };
                };

                return new UniqueCardStore({
                    baseObject: baseObject,
                    rootStore,
                    options,
                    initialData,
                    refreshOnCreate,
                    getStateFunction: (applicationFiltersStore) =>
                        buildDriversOnTheRiseState(applicationFiltersStore),
                    getDataFunction: async (
                        signalsService,
                        applicationFiltersStore,
                        state,
                    ): Promise<ISignalsDriversOnTheRiseResponse> => {
                        const requestState =
                            state ??
                            buildDriversOnTheRiseState(applicationFiltersStore);
                        const res = await signalsService.getDriversOnTheRise(
                            requestState,
                        );

                        return res;
                    },
                    component: DriversOnTheRise,
                });
            }
            throw new Error(
                `Tried creating a store for UniqueCard but no matching unique card found for: ${baseObject.dataEndpoint}`,
            );
        // Should only be hit if a new type is added but not yet handled
        default:
            throw new Error(
                `Tried creating a store for an unsupported component type: ${baseObject.objectType}`,
            );
    }
};

export const getFormattedDates = (
    dateStore: DatePickerComponentStore,
): (string | undefined)[] => {
    const dates = toIsoByDateReference(
        dateStore.beginDate,
        dateStore.endDate,
        dateStore.referenceOption,
    );

    const dayDiff = moment(dateStore.endDate).diff(
        moment(dateStore.beginDate),
        "days",
    );

    const comparePrevStart = moment(dateStore.beginDate)
        .subtract(dayDiff, "d")
        .startOf("date");
    const comparePrevEnd = moment(dateStore.endDate)
        .subtract(dayDiff + 1, "d")
        .endOf("date");

    const comparePrevDates = toIsoByDateReference(
        comparePrevStart,
        comparePrevEnd,
        dateStore.referenceOption,
    );

    return [
        dates.beginDate,
        dates.endDate,
        comparePrevDates.beginDate,
        comparePrevDates.endDate,
    ];
};

export const formatSmallCardResponse = (
    data: SmallCardRequestData,
): SmallCardFormattedData => {
    switch (data.type) {
        case SignalStatType.Number:
            const stat = getFormattedNumber(data.stat);
            return {
                stat,
                secondaryText: data.secondaryText,
                unformattedStat: data.stat ?? undefined,
                conversationCount: data.conversationCount,
                trendingProps: {
                    inputValue: data.change ?? 0,
                    isGrayOnly: true,
                },
            };
        case SignalStatType.Time:
            return {
                stat: getFormattedTime(data.stat),
                secondaryText: data.secondaryText,
                unformattedStat: data.stat ?? undefined,
                conversationCount: data.conversationCount,
                trendingProps: {
                    inputValue: data.change ?? 0,
                    showMinutes: true,
                },
            };
        case SignalStatType.Percentage:
            return {
                stat: getFormattedPercentage(data.stat),
                secondaryText: data.secondaryText,
                unformattedStat: data.stat ?? undefined,
                conversationCount: data.conversationCount,
                trendingProps: {
                    inputValue: data.change ?? 0,
                },
            };
        // Should only be hit if a new type is added but not yet handled
        default:
            return {
                stat: "",
                secondaryText: data.secondaryText,
                trendingProps: { inputValue: 0 },
            };
    }
};

export const getFormattedNumber = (conversations?: number | null) => {
    if (!conversations) return "N/A";
    return conversations.toLocaleString();
};

export const getFormattedTime = (averageCallDuration?: number | null) => {
    if (!averageCallDuration) return "N/A";
    const seconds = averageCallDuration / 1000;
    const duration = moment.duration(seconds, "seconds");

    const formatted: string[] = [];
    if (duration.hours()) formatted.push(`${duration.hours()}h`);
    if (duration.minutes()) formatted.push(`${duration.minutes()}m`);
    if (duration.seconds()) formatted.push(`${duration.seconds()}s`);

    return formatted.join(" ");
};

export const getFormattedPercentage = (conversationsPercent?: number | null) => {
    if (
        typeof conversationsPercent !== "number" ||
        !Number.isFinite(conversationsPercent)
    )
        return "N/A";

    return `${conversationsPercent.toFixed(2).replace(/\.00$/, "")}%`;
};
